import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Check, X, Clock, ArrowUpRight } from "lucide-react";
import JTLogo from "../components/JTLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("jt_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({ cap: 225, claimed: 0, remaining: 225, applications_total: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  const headers = { "X-Admin-Token": token };

  const verifyToken = async (t) => {
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Token": t } });
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!token) return;
    verifyToken(token).then((ok) => {
      if (ok) {
        setAuthed(true);
        localStorage.setItem("jt_admin_token", token);
      } else {
        setAuthed(false);
        localStorage.removeItem("jt_admin_token");
      }
    });
  }, []);

  const loadData = async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const [appsRes, statsRes] = await Promise.all([
        axios.get(`${API}/applications`, { headers, params }),
        axios.get(`${API}/applications/stats`),
      ]);
      setApplications(appsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) loadData();
  }, [authed, filter]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    const ok = await verifyToken(token);
    if (ok) {
      setAuthed(true);
      localStorage.setItem("jt_admin_token", token);
    } else {
      setLoginError("Invalid access token.");
    }
  };

  const onLogout = () => {
    setAuthed(false);
    setToken("");
    localStorage.removeItem("jt_admin_token");
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/applications/${id}/status`, { status }, { headers });
      await loadData();
    } catch (e) {
      console.error(e);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 lounge-vignette" data-testid="admin-login">
        <form
          onSubmit={onLogin}
          className="w-full max-w-[440px] text-center"
          style={{
            background: "#0A0A0A",
            border: "1px solid rgba(201,162,75,0.25)",
            borderRadius: 6,
            padding: "48px 36px",
          }}
        >
          <JTLogo size={64} />
          <div
            className="font-label mt-8"
            style={{ color: "var(--jt-gold)", fontSize: "11px" }}
          >
            ADMIN ACCESS
          </div>
          <h1
            className="font-display mt-4"
            style={{
              fontSize: "1.8rem",
              color: "var(--jt-ivory)",
              fontWeight: 500,
            }}
          >
            <span className="font-editorial italic">Reviewers only.</span>
          </h1>
          <div className="gold-divider mx-auto mt-6 mb-8" />

          <div className="text-left">
            <span
              className="font-label block mb-2"
              style={{ color: "var(--jt-silver)", fontSize: "10px" }}
            >
              ACCESS TOKEN
            </span>
            <input
              type="password"
              className="jt-input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              data-testid="admin-token-input"
              autoFocus
            />
          </div>

          {loginError && (
            <div
              className="font-editorial italic mt-4"
              style={{ color: "#D87E7E", fontSize: "14px" }}
              data-testid="admin-login-error"
            >
              {loginError}
            </div>
          )}

          <button type="submit" className="jt-cta w-full mt-8" data-testid="admin-login-submit">
            Enter
          </button>

          <Link
            to="/"
            className="jt-nav-link mt-8 inline-block"
            data-testid="admin-back-home"
          >
            ← Back to lounge
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header
        className="border-b px-6 sm:px-10 py-5 flex items-center justify-between sticky top-0 z-30"
        style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(14px)", borderColor: "rgba(201,162,75,0.15)" }}
      >
        <div className="flex items-center gap-3">
          <JTLogo size={32} />
          <span
            className="font-label gold-text hidden sm:inline"
            style={{ fontSize: "10px", letterSpacing: "0.4em" }}
          >
            JUMPSEAT · ADMIN
          </span>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/" className="jt-nav-link" data-testid="admin-view-site">View site</Link>
          <button onClick={onLogout} className="jt-nav-link" data-testid="admin-logout">Sign out</button>
        </div>
      </header>

      <main className="max-w-[1240px] mx-auto px-6 sm:px-10 py-12">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-12">
          <StatCard label="Total Applications" value={stats.applications_total} />
          <StatCard label="Approved" value={stats.claimed} />
          <StatCard label="Remaining" value={stats.remaining} />
          <StatCard label="Cap" value={stats.cap} />
        </div>

        {/* Filter */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <h2
            className="font-display"
            style={{ fontSize: "2rem", color: "var(--jt-ivory)", fontWeight: 500 }}
          >
            Applications
          </h2>
          <div className="flex gap-2">
            {["all", "pending", "approved", "declined"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="font-label"
                style={{
                  fontSize: "10px",
                  padding: "8px 16px",
                  border: `1px solid ${filter === f ? "var(--jt-gold)" : "rgba(201,162,75,0.25)"}`,
                  color: filter === f ? "var(--jt-gold)" : "var(--jt-silver)",
                  borderRadius: 999,
                  background: filter === f ? "rgba(201,162,75,0.08)" : "transparent",
                  cursor: "pointer",
                  transition: "all 200ms",
                }}
                data-testid={`admin-filter-${f}`}
              >
                {f.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-20 font-editorial italic" style={{ color: "var(--jt-silver)" }}>
            Loading applications…
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-20 font-editorial italic" style={{ color: "var(--jt-silver)" }} data-testid="admin-empty-state">
            No applications {filter !== "all" ? `with status "${filter}"` : "yet"}.
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-applications-list">
            {applications.map((app) => (
              <ApplicationRow
                key={app.id}
                app={app}
                onApprove={() => updateStatus(app.id, "approved")}
                onDecline={() => updateStatus(app.id, "declined")}
                onPending={() => updateStatus(app.id, "pending")}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const StatCard = ({ label, value }) => (
  <div
    className="p-6"
    style={{
      background: "linear-gradient(180deg, #0A0A0A 0%, #050505 100%)",
      border: "1px solid rgba(201,162,75,0.18)",
      borderRadius: 6,
    }}
  >
    <div
      className="font-label"
      style={{ color: "var(--jt-silver)", fontSize: "10px", marginBottom: 10 }}
    >
      {label.toUpperCase()}
    </div>
    <div
      className="font-display tabular"
      style={{ fontSize: "2.4rem", color: "var(--jt-ivory)", fontWeight: 500, lineHeight: 1 }}
    >
      {value}
    </div>
  </div>
);

const ApplicationRow = ({ app, onApprove, onDecline, onPending }) => {
  const [open, setOpen] = useState(false);
  const statusColor = {
    pending: "var(--jt-silver)",
    approved: "var(--jt-gold)",
    declined: "#D87E7E",
  }[app.status];

  return (
    <div
      style={{
        background: "#0A0A0A",
        border: "1px solid rgba(201,162,75,0.15)",
        borderRadius: 6,
      }}
      data-testid={`admin-app-row-${app.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-6 py-5 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-6 flex-1 min-w-0">
          <div
            className="font-label tabular"
            style={{ color: statusColor, fontSize: "10px", width: 80 }}
          >
            {app.status.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="font-display truncate"
              style={{ fontSize: "1.2rem", color: "var(--jt-ivory)", fontWeight: 500 }}
            >
              {app.first_name} {app.last_name}
            </div>
            <div
              className="font-editorial italic truncate"
              style={{ color: "var(--jt-silver)", fontSize: "14px", marginTop: 2 }}
            >
              {app.airline} · {app.base_airport} · {app.years_flying} {app.years_flying === 1 ? "year" : "years"} flying
            </div>
          </div>
          <div className="hidden md:block text-right">
            <div className="font-label" style={{ color: "var(--jt-mute)", fontSize: "10px" }}>
              {new Date(app.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </div>
          </div>
        </div>
        <ArrowUpRight
          size={16}
          style={{
            color: "var(--jt-gold)",
            transform: open ? "rotate(45deg)" : "none",
            transition: "transform 250ms",
            marginLeft: 16,
          }}
        />
      </button>

      {open && (
        <div className="px-6 pb-6 border-t" style={{ borderColor: "rgba(201,162,75,0.1)" }}>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-4 mt-5">
            <DetailField label="Email" value={app.email} />
            <DetailField label="Instagram" value={app.instagram_handle || "—"} />
            <DetailField label="Favorite Layover" value={app.favorite_layover_city || "—"} />
            <DetailField label="Heard via" value={app.referral_source || "—"} />
          </div>

          {app.lounge_doors_response && (
            <div className="mt-6">
              <div className="font-label" style={{ color: "var(--jt-gold)", fontSize: "10px", marginBottom: 8 }}>
                WHAT WOULD BRING THEM THROUGH THE LOUNGE DOORS?
              </div>
              <p
                className="font-editorial italic"
                style={{ color: "var(--jt-bone)", fontSize: "16px", lineHeight: 1.55 }}
              >
                {`"${app.lounge_doors_response}"`}
              </p>
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={onApprove}
              className="jt-cta-ghost"
              style={{ borderColor: "var(--jt-gold)", color: "var(--jt-gold)" }}
              data-testid={`admin-approve-${app.id}`}
            >
              <Check size={14} /> Approve
            </button>
            <button
              onClick={onPending}
              className="jt-cta-ghost"
              style={{ borderColor: "var(--jt-silver)", color: "var(--jt-silver)" }}
              data-testid={`admin-pending-${app.id}`}
            >
              <Clock size={14} /> Mark pending
            </button>
            <button
              onClick={onDecline}
              className="jt-cta-ghost"
              style={{ borderColor: "#7A3A3A", color: "#D87E7E" }}
              data-testid={`admin-decline-${app.id}`}
            >
              <X size={14} /> Decline
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailField = ({ label, value }) => (
  <div>
    <div className="font-label" style={{ color: "var(--jt-silver)", fontSize: "10px", marginBottom: 4 }}>
      {label.toUpperCase()}
    </div>
    <div style={{ color: "var(--jt-ivory)", fontSize: "15px", wordBreak: "break-word" }}>
      {value}
    </div>
  </div>
);
